/**
 * @file SignagePage.tsx
 * @description Fullscreen digital TV signage display engine for live in-venue menus and daily specials.
 * 
 * RESPONSIBILITIES & BEHAVIOR:
 * 1. Automatically rotates menu category panels on scheduled intervals.
 * 2. Applies daypart schedules, happy hour prices, and active campaign promotions in real-time.
 * 3. Enforces optional TV display hardware authentication passwords and session tokens.
 * 4. Renders responsive multi-column layouts adapting to 1080p and 4K TV displays.
 */

import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Clock, Info, Maximize2, Sparkles, Utensils, Lock, Unlock, Eye, EyeOff, ShieldAlert, Tv, KeyRound, CheckCircle2, X, ArrowLeft } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import { MenuSkeleton } from '../components/MenuSkeleton';
import { RTLWrapper } from '../components/layout/RTLWrapper';
import { useMenuData } from '../hooks/useMenuData';
import { useTranslation } from '../hooks/useTranslation';
import { ensureHex, optimizeImageUrl, hexToRgb } from '../lib/utils';
import { publicSupabase } from '../lib/supabase';
import { getCountryDefaultLocales } from '../lib/locales';
import { getSignageSessionKey, getSignageScreenIdKey } from '../lib/signage';
import type { CategoryWithItems, Item, ServiceHour, VenueWithMenu, SignageCategory, SignagePanel, AvailabilitySettings, HappyHourSettings, PromotionSettings } from '../types';

const DAY_KEYS = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];
const CLOCK_INTERVAL_MS = 30000;
const MAX_ITEMS_PER_CATEGORY = 5;

// Calculates number of panels and items fitting the current screen resolution and orientation
const getDisplayCapacity = (isPortrait = false) => {
  if (typeof window === 'undefined') return { panelsPerScreen: 3, itemsPerPanel: 5 };
  const { innerWidth: width, innerHeight: height } = window;
  if (isPortrait || height > width) {
    return {
      panelsPerScreen: 1,
      itemsPerPanel: height > 1200 ? 8 : 6,
    };
  }
  return {
    panelsPerScreen: width < 1100 ? 1 : width < 1600 ? 2 : 3,
    itemsPerPanel: height < 800 ? 4 : MAX_ITEMS_PER_CATEGORY,
  };
};

const toMinutes = (time?: string | null) => {
  if (!time) return null;
  const [hours, minutes] = time.split(':').map(Number);
  if (!Number.isFinite(hours) || !Number.isFinite(minutes)) return null;
  return hours * 60 + minutes;
};

const isWithinTimeRange = (currentTime: number, startTime?: string | null, endTime?: string | null) => {
  const start = toMinutes(startTime);
  const end = toMinutes(endTime);
  if (start === null || end === null) return true;
  if (start <= end) return currentTime >= start && currentTime <= end;
  return currentTime >= start || currentTime <= end;
};

const isScheduleActive = (
  settings: AvailabilitySettings | ServiceHour | null | undefined,
  currentDay: string,
  currentTime: number
) => {
  if (!settings) return true;
  if ('always_available' in settings && settings.always_available) return true;
  if (settings.active_days?.length && !settings.active_days.includes(currentDay)) return false;
  return isWithinTimeRange(currentTime, settings.start_time, settings.end_time);
};

const getHappyHourDiscount = (
  settings: HappyHourSettings | null | undefined,
  currentDay: string,
  currentTime: number
) => {
  if (!settings?.start_time || !settings.end_time) return 0;
  if (settings.active_days?.length && !settings.active_days.includes(currentDay)) return 0;
  if (!isWithinTimeRange(currentTime, settings.start_time, settings.end_time)) return 0;
  return settings.discount_percentage || 20;
};

const getPromotionPrice = (
  promotions: PromotionSettings[],
  categoryId: string,
  itemId: string,
  originalPrice: number,
  currentDay: string,
  currentTime: number,
) => promotions.reduce((bestPrice, promotion) => {
  if (promotion.active === false) return bestPrice;
  if (promotion.days?.length && !promotion.days.includes(currentDay)) return bestPrice;
  if (!isWithinTimeRange(currentTime, promotion.start_time, promotion.end_time)) return bestPrice;

  const targetIds = promotion.target_ids || [];
  if (promotion.target_type === 'categories' && !targetIds.includes(categoryId)) return bestPrice;
  if (promotion.target_type === 'items' && !targetIds.includes(itemId)) return bestPrice;

  const discountValue = Math.max(0, Number(promotion.discount_value || 0));
  const candidate = promotion.discount_type === 'fixed'
    ? Math.max(0, originalPrice - discountValue)
    : originalPrice * (1 - Math.min(discountValue, 100) / 100);

  return Math.min(bestPrice, candidate);
}, originalPrice);

const getSignageCategories = (venue: VenueWithMenu | null, now: Date): SignageCategory[] => {
  if (!venue?.categories) return [];

  let currentDay = DAY_KEYS[now.getDay()];
  let currentTime = now.getHours() * 60 + now.getMinutes();

  if (venue?.timezone) {
    try {
      const formatter = new Intl.DateTimeFormat('en-US', {
        timeZone: venue.timezone,
        hour12: false,
        hourCycle: 'h23',
        weekday: 'short',
        hour: 'numeric',
        minute: 'numeric'
      });
      const parts = formatter.formatToParts(now);
      const partMap = Object.fromEntries(parts.map(p => [p.type, p.value]));
      if (partMap.weekday) {
        currentDay = partMap.weekday.toLowerCase();
      }
      if (partMap.hour && partMap.minute) {
        currentTime = parseInt(partMap.hour, 10) * 60 + parseInt(partMap.minute, 10);
      }
    } catch (e) {
      console.error('Error formatting timezone for signage:', e);
    }
  }

  const promotions = (venue.theme_settings?.campaigns?.promotions || []) as PromotionSettings[];

  return venue.categories
    .filter(category => {
      if (category.service_hour_id && category.service_hours) {
        return isScheduleActive(category.service_hours, currentDay, currentTime);
      }
      return isScheduleActive(category.availability_settings, currentDay, currentTime);
    })
    .map(category => {
      const discount = getHappyHourDiscount(category.happy_hour_settings, currentDay, currentTime);
      const items = (category.items || [])
        .map(item => {
          const originalPrice = Number(item.price);
          if (!Number.isFinite(originalPrice)) return item;

          const happyHourPrice = discount > 0
            ? originalPrice * (1 - Math.min(Math.max(discount, 0), 100) / 100)
            : originalPrice;
          const promotionPrice = getPromotionPrice(
            promotions,
            category.id,
            item.id,
            originalPrice,
            currentDay,
            currentTime,
          );
          const price = Math.min(happyHourPrice, promotionPrice);

          return price < originalPrice
            ? { ...item, original_price: originalPrice, price, is_happy_hour: true }
            : item;
        })
        .filter(item => {
          if (item.is_available === false || item.stock_status === 'OUT_OF_STOCK') return false;
          if (item.service_hour_id && item.service_hours) {
            return isScheduleActive(item.service_hours, currentDay, currentTime);
          }
          return isScheduleActive(item.availability_settings, currentDay, currentTime);
        });

      return { ...category, items };
    })
    .filter(category => category.items.length > 0);
};

const buildSignagePages = (
  categories: SignageCategory[],
  panelsPerScreen: number,
  itemsPerPanel: number,
) => {
  const panels = categories.flatMap(category => {
    const panelCount = Math.ceil(category.items.length / itemsPerPanel);
    return Array.from({ length: panelCount }, (_, panelIndex): SignagePanel => ({
      ...category,
      items: category.items.slice(
        panelIndex * itemsPerPanel,
        (panelIndex + 1) * itemsPerPanel,
      ),
      panelIndex,
      panelCount,
      totalItemCount: category.items.length,
    }));
  });

  const pages: SignagePanel[][] = [];
  for (let index = 0; index < panels.length; index += panelsPerScreen) {
    pages.push(panels.slice(index, index + panelsPerScreen));
  }
  return pages;
};

const getReadableAccent = (color: string | null | undefined) => {
  const hex = ensureHex(color, '#f5b84b').replace('#', '');
  const r = parseInt(hex.slice(0, 2), 16);
  const g = parseInt(hex.slice(2, 4), 16);
  const b = parseInt(hex.slice(4, 6), 16);
  const luminance = (0.2126 * r + 0.7152 * g + 0.0722 * b) / 255;
  return luminance < 0.38 ? '#f5b84b' : `#${hex}`;
};

export const SignagePage: React.FC<{ customDomain?: string }> = ({ customDomain }) => {
  const params = useParams<{ venueSlug?: string }>();
  const location = useLocation();
  const navigate = useNavigate();

  // Resolves the correct venue slug across paths (/:slug/signage or query param or saved slug)
  const venueSlug = useMemo(() => {
    if (params.venueSlug) return params.venueSlug;
    const searchParams = new URLSearchParams(location.search);
    const querySlug = searchParams.get('venue');
    if (querySlug) return querySlug;
    const pathSegments = location.pathname.split('/').filter(Boolean);
    if (pathSegments.length > 0 && pathSegments[0] !== 'signage') {
      return pathSegments[0];
    }
    if (typeof window !== 'undefined') {
      return localStorage.getItem('tablegreet_last_signage_slug') || undefined;
    }
    return undefined;
  }, [params.venueSlug, location.search, location.pathname]);

  const { venue, isLoading, error } = useMenuData(venueSlug, customDomain);

  // Save last successful venue slug for instant TV power-on resumption
  useEffect(() => {
    if (venue?.slug && typeof window !== 'undefined') {
      localStorage.setItem('tablegreet_last_signage_slug', venue.slug);
    }
  }, [venue?.slug]);

  const formatPrice = useCallback((price: number | string | null | undefined) => {
    const numericPrice = Number(price);
    if (!Number.isFinite(numericPrice)) return '';
    const currency = venue?.currency || 'MAD';
    const defaultLocale = venue?.default_locale || getCountryDefaultLocales(venue?.country).defaultLocale;
    try {
      return new Intl.NumberFormat(defaultLocale, {
        style: 'currency',
        currency,
        maximumFractionDigits: Number.isInteger(numericPrice) ? 0 : 2,
      }).format(numericPrice);
    } catch {
      return `${currency} ${numericPrice}`;
    }
  }, [venue?.currency, venue?.default_locale, venue?.country]);

  const { t, currentLanguage } = useTranslation();
  const screenRef = useRef<HTMLElement>(null);
  const [now, setNow] = useState(() => new Date());
  const [activeIndex, setActiveIndex] = useState(0);
  const [displayCapacity, setDisplayCapacity] = useState(getDisplayCapacity);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isAuthorized, setIsAuthorized] = useState(false);
  const [runtimeOrigin, setRuntimeOrigin] = useState<string | null>(null);
  const [passwordInput, setPasswordInput] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isVerifying, setIsVerifying] = useState(true);
  const [authError, setAuthError] = useState('');

  const [authTab, setAuthTab] = useState<'password' | 'code'>('password');
  const [pairingCodeInput, setPairingCodeInput] = useState('');
  const [isPairDialogOpen, setIsPairDialogOpen] = useState(false);
  const [activeScreenId, setActiveScreenId] = useState<string | null>(null);
  const [activeScreenName, setActiveScreenName] = useState<string | null>(null);

  const signageSettings = venue?.theme_settings?.signage;
  const hasSignageAccess = ['PRO', 'MAX', 'ENTERPRISE'].includes(String(venue?.tier || '').toUpperCase());
  const isSignageEnabled = hasSignageAccess && (signageSettings?.enabled ?? false);
  const passwordRequired = signageSettings?.password_required ?? false;

  const rotationIntervalSec = Math.max(5, signageSettings?.rotation_interval_seconds || 15);
  const rotationIntervalMs = rotationIntervalSec * 1000;
  const showQr = signageSettings?.show_qr !== false;
  const showClock = signageSettings?.show_clock !== false;
  const showDescription = signageSettings?.show_description !== false;
  const showFeatured = signageSettings?.show_featured !== false;
  const isPortrait = signageSettings?.orientation === 'portrait';

  useEffect(() => {
    if (!venue?.id) return;
    const storedScreenId = localStorage.getItem(getSignageScreenIdKey(venue.id));
    if (storedScreenId) {
      setActiveScreenId(storedScreenId);
    }
  }, [venue?.id]);

  useEffect(() => {
    if (!venue) return;

    const checkAuth = async () => {
      if (!isSignageEnabled) {
        setIsAuthorized(false);
        setIsVerifying(false);
        return;
      }

      if (!passwordRequired) {
        setIsAuthorized(true);
        setIsVerifying(false);
        return;
      }

      const sessionKey = getSignageSessionKey(venue.id);
      const storedToken = sessionStorage.getItem(sessionKey) || (typeof window !== 'undefined' ? localStorage.getItem(sessionKey) : null);
      if (storedToken) {
        try {
          const { data, error: verificationError } = await publicSupabase.rpc('verify_signage_session', {
            p_venue_id: venue.id,
            p_token: storedToken,
          });
          if (verificationError) throw verificationError;
          if (data === true) {
            setIsAuthorized(true);
            setAuthError('');
            // Keep synchronized across both storages
            sessionStorage.setItem(sessionKey, storedToken);
            localStorage.setItem(sessionKey, storedToken);
          } else {
            setIsAuthorized(false);
            sessionStorage.removeItem(sessionKey);
            localStorage.removeItem(sessionKey);
          }
        } catch (err) {
          console.error('Signage auth verification failed:', err);
          setIsAuthorized(false);
          setAuthError('Unable to verify this display. Check the connection and try again.');
        }
      } else {
        setIsAuthorized(false);
      }
      setIsVerifying(false);
    };

    void checkAuth();
  }, [venue, passwordRequired, isSignageEnabled, signageSettings?.updated_at]);

  const handlePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = passwordInput.trim();
    if (!venue || !passwordRequired || !trimmed) return;

    setIsVerifying(true);
    setAuthError('');

    try {
      const { data: sessionToken, error: authorizationError } = await publicSupabase.rpc('authorize_signage', {
        p_venue_id: venue.id,
        p_password: trimmed,
      });
      if (authorizationError) throw authorizationError;

      if (typeof sessionToken === 'string' && sessionToken) {
        const sessionKey = getSignageSessionKey(venue.id);
        sessionStorage.setItem(sessionKey, sessionToken);
        localStorage.setItem(sessionKey, sessionToken);
        setIsAuthorized(true);
        setPasswordInput('');
        setAuthError('');
      } else {
        setAuthError('Incorrect PIN or password. Please try again.');
      }
    } catch (err: any) {
      setAuthError(err.message || 'An error occurred. Please try again.');
      console.error(err);
    } finally {
      setIsVerifying(false);
    }
  };

  const handlePairCodeSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!venue || !pairingCodeInput.trim()) return;

    setIsVerifying(true);
    setAuthError('');

    try {
      const code = pairingCodeInput.trim().toUpperCase();
      const userAgent = typeof navigator !== 'undefined' ? navigator.userAgent : null;
      const { data, error: pairError } = await publicSupabase.rpc('pair_signage_screen', {
        p_code: code,
        p_venue_id: venue.id,
        p_user_agent: userAgent,
      });

      if (pairError) throw pairError;

      if (data && typeof data === 'object' && 'token' in data && 'screen_id' in data) {
        const pairResult = data as { screen_id: string; token: string; name?: string };
        const sessionKey = getSignageSessionKey(venue.id);
        sessionStorage.setItem(sessionKey, pairResult.token);
        localStorage.setItem(sessionKey, pairResult.token);
        localStorage.setItem(getSignageScreenIdKey(venue.id), pairResult.screen_id);
        setActiveScreenId(pairResult.screen_id);
        if (pairResult.name) setActiveScreenName(pairResult.name);
        setIsAuthorized(true);
        setPairingCodeInput('');
        setIsPairDialogOpen(false);
        setAuthError('');
      } else {
        setAuthError('Invalid or expired pairing code. Check your admin dashboard.');
      }
    } catch (err: any) {
      console.error('Pairing error:', err);
      setAuthError(err.message || 'Failed to pair screen. Please try again.');
    } finally {
      setIsVerifying(false);
    }
  };

  const handleLock = async () => {
    if (!venue) return;
    const sessionKey = getSignageSessionKey(venue.id);
    const sessionToken = sessionStorage.getItem(sessionKey) || localStorage.getItem(sessionKey);
    sessionStorage.removeItem(sessionKey);
    localStorage.removeItem(sessionKey);
    if (sessionToken) {
      const { error: revokeError } = await publicSupabase.rpc('revoke_signage_session', {
        p_venue_id: venue.id,
        p_token: sessionToken,
      });
      if (revokeError) console.warn('Unable to revoke signage session:', revokeError);
    }
    setIsAuthorized(false);
    setPasswordInput('');
  };

  useEffect(() => {
    if (!venue?.id || !isAuthorized) return;

    const screenId = activeScreenId || (typeof window !== 'undefined' ? localStorage.getItem(getSignageScreenIdKey(venue.id)) : null);
    if (!screenId) return;

    const sendHeartbeat = async () => {
      try {
        const sessionKey = getSignageSessionKey(venue.id);
        const sessionToken = sessionStorage.getItem(sessionKey) || (typeof window !== 'undefined' ? localStorage.getItem(sessionKey) : null);
        const userAgent = typeof navigator !== 'undefined' ? navigator.userAgent : null;
        await publicSupabase.rpc('signage_heartbeat', {
          p_screen_id: screenId,
          p_venue_id: venue.id,
          p_token: sessionToken || null,
          p_user_agent: userAgent,
        });
      } catch (err) {
        console.debug('Signage heartbeat ping:', err);
      }
    };

    void sendHeartbeat();
    const heartbeatTimer = window.setInterval(sendHeartbeat, 30000);
    return () => window.clearInterval(heartbeatTimer);
  }, [venue?.id, isAuthorized, activeScreenId]);

  useEffect(() => {
    if (!venue?.id) return;

    const channel = publicSupabase.channel(`signage:${venue.id}`);
    channel
      .on('broadcast', { event: 'screen_action' }, (payload) => {
        const { action, screenId } = (payload.payload as { action?: string; screenId?: string }) || {};
        const currentScreenId = localStorage.getItem(getSignageScreenIdKey(venue.id));

        if (!screenId || screenId === currentScreenId) {
          if (action === 'reload') {
            window.location.reload();
          } else if (action === 'lock') {
            void handleLock();
          }
        }
      })
      .subscribe();

    return () => {
      void publicSupabase.removeChannel(channel);
    };
  }, [venue?.id]);

  useEffect(() => {
    const clock = window.setInterval(() => setNow(new Date()), CLOCK_INTERVAL_MS);
    return () => window.clearInterval(clock);
  }, []);

  useEffect(() => {
    setRuntimeOrigin(window.location.origin);
  }, []);

  useEffect(() => {
    const handleResize = () => setDisplayCapacity(getDisplayCapacity(isPortrait));
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isPortrait]);

  const categories = useMemo(() => getSignageCategories(venue, now), [venue, now]);
  const categoryPages = useMemo(
    () => buildSignagePages(categories, displayCapacity.panelsPerScreen, displayCapacity.itemsPerPanel),
    [categories, displayCapacity.itemsPerPanel, displayCapacity.panelsPerScreen],
  );
  const activePageIndex = activeIndex % Math.max(categoryPages.length, 1);
  const visiblePanels = categoryPages[activePageIndex] || [];
  const allVisibleItems = visiblePanels.flatMap(category => category.items);
  const featuredItem = allVisibleItems.find(item => item.is_featured || item.image_url) || allVisibleItems[0] || categories[0]?.items[0];
  const accentColor = getReadableAccent(venue?.primary_color);

  const venueTime = useMemo(() => {
    try {
      return new Intl.DateTimeFormat(venue?.default_locale || getCountryDefaultLocales(venue?.country).defaultLocale, {
        hour: '2-digit',
        minute: '2-digit',
        timeZone: venue?.timezone || undefined,
      }).format(now);
    } catch {
      return now.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    }
  }, [now, venue?.default_locale, venue?.country, venue?.timezone]);

  const guestMenuUrl = useMemo(() => {
    if (customDomain) {
      return runtimeOrigin || `https://${customDomain}`;
    }
    const slug = venue?.slug || venueSlug || '';
    const origin = 'https://venue.tablegreet.com';
    return `${origin}/${slug}`;
  }, [customDomain, runtimeOrigin, venue?.slug, venueSlug]);

  useEffect(() => {
    if (activeIndex >= categoryPages.length) setActiveIndex(0);
  }, [activeIndex, categoryPages.length]);

  useEffect(() => {
    if (categoryPages.length <= 1) return;
    const rotation = window.setInterval(() => {
      setActiveIndex(index => (index + 1) % categoryPages.length);
    }, rotationIntervalMs);
    return () => window.clearInterval(rotation);
  }, [categoryPages.length, rotationIntervalMs]);

  useEffect(() => {
    if (!venue) return;
    document.title = `${venue.name} | TV Menu`;
  }, [venue]);

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(Boolean(document.fullscreenElement));
    };
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  const enterFullscreen = async () => {
    try {
      await screenRef.current?.requestFullscreen();
    } catch (err) {
      console.error('Unable to enter fullscreen mode:', err);
    }
  };

  if (!venueSlug && !isLoading) {
    return (
      <div className="min-h-screen bg-[#070707] flex flex-col items-center justify-center p-6 text-center">
        <div className="max-w-md w-full p-8 rounded-3xl border border-white/10 bg-white/[0.02] backdrop-blur-md shadow-2xl">
          <div className="w-16 h-16 bg-brand-primary/10 rounded-2xl flex items-center justify-center mb-5 mx-auto border border-brand-primary/20 text-brand-primary">
            <Tv className="w-8 h-8" />
          </div>
          <h1 className="text-2xl font-bold text-white mb-2">TableGreet TV Signage</h1>
          <p className="text-gray-400 text-sm mb-6">
            Please specify a venue slug in the URL to start display mode.
          </p>
          <button
            type="button"
            onClick={() => navigate('/')}
            className="w-full py-3 rounded-xl bg-brand-primary text-black font-black uppercase tracking-wider text-xs flex items-center justify-center gap-2"
          >
            <ArrowLeft className="w-4 h-4" /> Go to Screen Launcher
          </button>
        </div>
      </div>
    );
  }

  if (isLoading) return <MenuSkeleton />;

  if (!venue) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex flex-col items-center justify-center p-6 text-center">
        <div className="w-20 h-20 bg-red-500/10 rounded-full flex items-center justify-center mb-6">
          <Info className="w-10 h-10 text-red-500" />
        </div>
        <h1 className="text-2xl font-bold text-white mb-2">Signage Menu Not Found</h1>
        <p className="text-gray-400 max-w-sm mb-6">
          {error || `The TV menu could not be loaded for "${venueSlug}".`}
        </p>
        <button
          type="button"
          onClick={() => navigate('/')}
          className="px-6 py-2.5 rounded-xl border border-white/10 bg-white/5 text-white text-xs font-bold hover:bg-white/10 transition-colors"
        >
          Select Another Venue
        </button>
      </div>
    );
  }

  if (!isSignageEnabled) {
    return (
      <div className="min-h-screen bg-[#070707] flex flex-col items-center justify-center p-6 text-center relative overflow-hidden">
        <div className="absolute inset-0 bg-[linear-gradient(135deg,rgba(245,184,75,0.05)_0%,transparent_50%),linear-gradient(180deg,#0a0a0a_0%,#050505_100%)]" />
        <div className="relative z-10 max-w-md w-full mx-auto p-8 rounded-3xl border border-white/10 bg-white/[0.02] backdrop-blur-md shadow-2xl">
          <div className="w-16 h-16 bg-amber-500/10 rounded-2xl flex items-center justify-center mb-6 mx-auto border border-amber-500/20 text-amber-500">
            <ShieldAlert className="w-8 h-8" />
          </div>
          <h1 className="text-3xl font-black text-white mb-3 tracking-tight">TV Signage Disabled</h1>
          <p className="text-gray-400 text-sm font-medium leading-relaxed mb-6">
            The signage display is currently turned off for <strong className="text-white font-bold">{venue.name}</strong>. You can enable it anytime in the Admin Dashboard.
          </p>
          <div className="inline-flex items-center gap-2 rounded-full border border-white/5 bg-white/5 px-4 py-2 text-xs font-bold text-gray-500">
            <span>tablegreet.com</span>
          </div>
        </div>
      </div>
    );
  }

  if (passwordRequired && !isAuthorized) {
    if (isVerifying) {
      return <MenuSkeleton />;
    }

    return (
      <div className="min-h-screen bg-[#070707] flex flex-col items-center justify-center p-6 text-center relative overflow-hidden">
        <div className="absolute inset-0 bg-[linear-gradient(135deg,rgba(245,184,75,0.05)_0%,transparent_50%),linear-gradient(180deg,#0a0a0a_0%,#050505_100%)]" />

        <div className="relative z-10 max-w-md w-full mx-auto p-8 rounded-3xl border border-white/10 bg-white/[0.02] backdrop-blur-md shadow-2xl">
          <div className="w-16 h-16 bg-brand-primary/10 rounded-2xl flex items-center justify-center mb-5 mx-auto border border-brand-primary/20 text-brand-primary">
            {authTab === 'password' ? <Lock className="w-8 h-8" /> : <KeyRound className="w-8 h-8" />}
          </div>
          <h1 className="text-3xl font-black text-white mb-2 tracking-tight">Protected Display</h1>
          <p className="text-gray-400 text-sm font-medium leading-relaxed mb-6">
            Authenticate to start the big-screen menu for <strong className="text-white font-bold">{venue.name}</strong>.
          </p>

          <div className="grid grid-cols-2 p-1 bg-black/40 border border-white/10 rounded-xl mb-5">
            <button
              type="button"
              onClick={() => { setAuthTab('password'); setAuthError(''); }}
              className={`py-2 text-xs font-bold rounded-lg transition-all ${
                authTab === 'password' ? 'bg-brand-primary text-black shadow' : 'text-gray-400 hover:text-white'
              }`}
            >
              TV PIN
            </button>
            <button
              type="button"
              onClick={() => { setAuthTab('code'); setAuthError(''); }}
              className={`py-2 text-xs font-bold rounded-lg transition-all ${
                authTab === 'code' ? 'bg-brand-primary text-black shadow' : 'text-gray-400 hover:text-white'
              }`}
            >
              Pairing Code
            </button>
          </div>

          {authTab === 'password' ? (
            <form onSubmit={handlePasswordSubmit} className="space-y-4">
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={passwordInput}
                  onChange={(e) => setPasswordInput(e.target.value)}
                  placeholder="Enter TV PIN"
                  className="w-full h-12 rounded-xl border border-white/10 bg-black/40 px-4 pr-12 font-bold tracking-widest text-white outline-none transition-colors placeholder:text-gray-600 focus:border-brand-primary placeholder:tracking-normal"
                  required
                  disabled={isVerifying}
                  autoComplete="current-password"
                  spellCheck={false}
                  maxLength={128}
                  aria-describedby={authError ? 'signage-password-error' : undefined}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-white transition-colors"
                  disabled={isVerifying}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>

              {authError && (
                <p id="signage-password-error" role="alert" className="text-xs font-bold text-red-400 text-left px-1">
                  {authError}
                </p>
              )}

              <button
                type="submit"
                disabled={isVerifying}
                className="w-full h-12 rounded-xl bg-brand-primary text-black font-black uppercase tracking-widest text-sm transition-transform active:scale-[0.98] disabled:opacity-50 flex items-center justify-center gap-2 cursor-pointer"
              >
                {isVerifying ? (
                  <div className="w-5 h-5 border-2 border-black border-t-transparent rounded-full animate-spin" />
                ) : (
                  <>
                    <Unlock size={16} />
                    Authorize Display
                  </>
                )}
              </button>
            </form>
          ) : (
            <form onSubmit={handlePairCodeSubmit} className="space-y-4">
              <div>
                <input
                  type="text"
                  value={pairingCodeInput}
                  onChange={(e) => setPairingCodeInput(e.target.value.toUpperCase())}
                  placeholder="e.g. A3F8K2"
                  className="w-full h-12 rounded-xl border border-white/10 bg-black/40 px-4 font-mono font-black text-center tracking-[0.35em] text-lg text-white outline-none transition-colors placeholder:text-gray-600 focus:border-brand-primary uppercase"
                  required
                  disabled={isVerifying}
                  maxLength={6}
                  autoComplete="off"
                  spellCheck={false}
                />
                <p className="mt-2 text-[11px] text-gray-500 text-left px-1">
                  Generate a 6-character code in your TableGreet Admin Dashboard under Signage Screens.
                </p>
              </div>

              {authError && (
                <p role="alert" className="text-xs font-bold text-red-400 text-left px-1">
                  {authError}
                </p>
              )}

              <button
                type="submit"
                disabled={isVerifying || pairingCodeInput.trim().length < 4}
                className="w-full h-12 rounded-xl bg-brand-primary text-black font-black uppercase tracking-widest text-sm transition-transform active:scale-[0.98] disabled:opacity-50 flex items-center justify-center gap-2 cursor-pointer"
              >
                {isVerifying ? (
                  <div className="w-5 h-5 border-2 border-black border-t-transparent rounded-full animate-spin" />
                ) : (
                  <>
                    <Tv size={16} />
                    Pair & Launch
                  </>
                )}
              </button>
            </form>
          )}
        </div>
      </div>
    );
  }

  return (
    <RTLWrapper languageCode={currentLanguage}>
      <main
        ref={screenRef}
        className="relative h-screen w-screen overflow-hidden bg-[#070707] text-white"
        style={{
          ['--color-brand-primary' as string]: accentColor,
          ['--color-brand-primary-rgb' as string]: hexToRgb(accentColor),
          ['--signage-accent' as string]: accentColor,
          ['--brand-radius' as string]: `${venue.border_radius || 16}px`,
        } as React.CSSProperties}
      >
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background: `radial-gradient(circle at 12% 0%, rgba(var(--color-brand-primary-rgb), 0.12) 0%, transparent 45%), radial-gradient(circle at 88% 100%, rgba(var(--color-brand-primary-rgb), 0.05) 0%, transparent 45%), #08080a`,
          }}
        />
        <div className="absolute inset-x-0 top-0 h-1 bg-[var(--signage-accent)]" />

        <section className="relative z-10 flex h-full flex-col px-[clamp(1.25rem,2.8vw,3.5rem)] py-[clamp(0.75rem,2vh,2.5rem)]">
          <header className="relative grid h-[clamp(5rem,13vh,8rem)] shrink-0 grid-cols-[minmax(0,1fr)_auto] items-center gap-[clamp(0.75rem,1.5vw,1.75rem)] border-b border-white/10 pb-[clamp(0.75rem,2vh,1.5rem)] xl:grid-cols-[minmax(0,1fr)_auto_auto_auto]">
            {categoryPages.length > 1 && (
              <div className="absolute bottom-0 inset-x-0 h-[2px] bg-white/5 overflow-hidden">
                <motion.div
                  key={activePageIndex}
                  initial={{ width: '0%' }}
                  animate={{ width: '100%' }}
                  transition={{ duration: rotationIntervalSec, ease: 'linear' }}
                  className="h-full bg-[var(--signage-accent)] shadow-[0_0_12px_rgba(var(--color-brand-primary-rgb),0.8)]"
                />
              </div>
            )}
            <div className="flex min-w-0 items-center gap-6">
              <div
                className="relative h-[clamp(3.75rem,9.5vh,6.25rem)] w-[clamp(3.75rem,9.5vh,6.25rem)] shrink-0 overflow-hidden border border-white/15 bg-white/[0.04] shadow-2xl flex items-center justify-center backdrop-blur-md"
                style={{ borderRadius: `calc(var(--brand-radius) * 0.75)` }}
              >
                <Utensils className="h-1/2 w-1/2 text-brand-primary" />
                {venue.logo_url && (
                  <img
                    src={venue.logo_url}
                    alt={venue.name}
                    onError={(event) => {
                      event.currentTarget.style.display = 'none';
                    }}
                    className="absolute inset-0 h-full w-full bg-white object-contain p-2"
                  />
                )}
              </div>

              <div className="min-w-0">
                <div className="mb-2 flex items-center gap-3">
                  <span className="relative flex h-2.5 w-2.5">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                    <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-400 shadow-[0_0_12px_rgba(52,211,153,0.9)]" />
                  </span>
                  <span className="text-[11px] font-black uppercase tracking-[0.32em] text-emerald-400">
                    Live Menu
                  </span>
                </div>
                <h1 className="truncate text-[clamp(2.1rem,3.4vw,3.85rem)] font-black leading-none tracking-tight text-white">
                  {venue.name}
                </h1>
                {showDescription && venue.description && (
                  <p className="mt-2 hidden line-clamp-1 max-w-5xl text-[clamp(0.85rem,1.2vw,1.35rem)] font-medium text-white/60 lg:block">
                    {venue.description}
                  </p>
                )}
              </div>
            </div>

            {showClock && (
              <div
                className="hidden xl:flex items-center gap-3.5 border border-white/10 bg-white/[0.03] backdrop-blur-md px-5 py-3.5 shadow-lg"
                style={{ borderRadius: `calc(var(--brand-radius) * 0.75)` }}
              >
                <Clock className="h-7 w-7 text-brand-primary" />
                <div>
                  <p className="text-4xl font-black leading-none font-mono tabular-nums text-white">
                    {venueTime}
                  </p>
                  <p className="mt-1 text-[10px] font-black uppercase tracking-[0.25em] text-white/40">
                    Updated Live
                  </p>
                </div>
              </div>
            )}

            {showQr && (
              <div
                className="hidden items-center gap-4 bg-white p-2.5 pr-5 text-black shadow-2xl lg:flex border border-white/20"
                style={{ borderRadius: `calc(var(--brand-radius) * 0.75)` }}
              >
                <QRCodeSVG value={guestMenuUrl} size={70} bgColor="#ffffff" fgColor="#08080a" level="M" />
                <div className="hidden 2xl:block w-36">
                  <p className="text-[9px] font-black uppercase tracking-[0.22em] text-black/50">Scan Menu</p>
                  <p className="mt-1 text-sm font-black leading-snug text-black">Open on your phone</p>
                </div>
              </div>
            )}

            <div className="flex items-center gap-2.5">
              <button
                type="button"
                onClick={() => setIsPairDialogOpen(true)}
                className="flex h-11 w-11 items-center justify-center rounded-xl border border-white/10 bg-white/[0.03] text-white/80 shadow-md transition-all hover:bg-white/10 hover:text-white cursor-pointer"
                title={activeScreenName ? `Connected Screen: ${activeScreenName}` : "Device Pairing"}
                aria-label="Device Pairing"
              >
                <Tv className="h-5 w-5 text-brand-primary" />
              </button>
              {passwordRequired && (
                <button
                  type="button"
                  onClick={() => void handleLock()}
                  className="flex h-11 w-11 items-center justify-center rounded-xl border border-white/10 bg-white/[0.03] text-white/80 shadow-md transition-all hover:bg-white/10 hover:text-red-400 cursor-pointer"
                  title="Lock TV board"
                  aria-label="Lock TV board"
                >
                  <Lock className="h-5 w-5 text-brand-primary" />
                </button>
              )}
              {!isFullscreen && (
                <button
                  type="button"
                  onClick={enterFullscreen}
                  className="flex min-h-11 items-center gap-2 rounded-xl border border-white/10 bg-white/[0.03] px-3.5 text-[10px] font-black uppercase tracking-widest text-white/80 shadow-md transition-all hover:bg-white/10 hover:text-white cursor-pointer"
                >
                  <Maximize2 className="h-4 w-4 text-brand-primary" />
                  <span className="hidden xl:inline">Full Screen</span>
                </button>
              )}
            </div>
          </header>

          {categories.length === 0 ? (
            <div className="flex flex-1 items-center justify-center text-center">
              <div>
                <Sparkles className="mx-auto mb-5 h-14 w-14 text-brand-primary" />
                <h2 className="text-4xl font-black text-white">No items are available right now</h2>
                <p className="mt-3 text-xl text-white/60">Please check back during service hours.</p>
              </div>
            </div>
          ) : (
            <div className="flex min-h-0 flex-1 flex-col gap-[clamp(0.75rem,2vh,1.5rem)] pt-[clamp(0.5rem,1.5vh,1.5rem)]">
              <AnimatePresence mode="wait">
                <motion.div
                  key={activePageIndex}
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -16 }}
                  transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
                  className={`grid min-h-0 flex-1 gap-5 justify-center ${
                    visiblePanels.length === 1
                      ? 'grid-cols-1 max-w-3xl w-full mx-auto'
                      : visiblePanels.length === 2
                      ? 'grid-cols-2 max-w-6xl w-full mx-auto'
                      : 'grid-cols-3 w-full'
                  }`}
                >
                  {visiblePanels.map((category, columnIndex) => (
                    <section
                      key={`${category.id}-${category.panelIndex}`}
                      className="grid min-h-0 grid-rows-[auto_1fr] overflow-hidden border border-white/10 bg-[#0e0e12]/95 backdrop-blur-xl shadow-2xl relative"
                      style={{ borderRadius: 'var(--brand-radius)' }}
                    >
                      <div className="absolute inset-x-0 top-0 h-[3px] bg-[var(--signage-accent)]" />

                      <div className="border-b border-white/10 bg-gradient-to-b from-white/[0.07] to-white/[0.02] px-[clamp(1.1rem,2vw,1.9rem)] py-[clamp(0.85rem,1.8vh,1.35rem)]">
                        <div className="mb-2 flex items-center justify-between gap-4">
                          <span className="inline-flex items-center gap-1.5 rounded-md bg-[var(--signage-accent)]/15 border border-[var(--signage-accent)]/30 px-2.5 py-0.5 text-[10px] font-black uppercase tracking-[0.25em] text-[var(--signage-accent)]">
                            Menu {activePageIndex + 1}.{columnIndex + 1}
                          </span>
                          <span className="text-xs font-bold text-white/50 tracking-wider">
                            {category.panelCount > 1
                              ? `${category.panelIndex * displayCapacity.itemsPerPanel + 1}-${Math.min((category.panelIndex + 1) * displayCapacity.itemsPerPanel, category.totalItemCount)} of ${category.totalItemCount}`
                              : `${category.totalItemCount} items`}
                          </span>
                        </div>
                        <h2 className="line-clamp-1 text-[clamp(1.55rem,2.7vw,3.1rem)] font-black leading-tight tracking-tight text-white">
                          {t(category, 'name')}
                        </h2>
                      </div>

                      <div
                        className="grid min-h-0 divide-y divide-white/[0.06] overflow-hidden"
                        style={{ gridTemplateRows: `repeat(${category.items.length}, minmax(0, 1fr))` }}
                      >
                        {category.items.map((item, itemIndex) => (
                          <motion.article
                            key={item.id}
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: (columnIndex * 0.04) + (itemIndex * 0.03), duration: 0.35 }}
                            className={`grid min-h-0 items-center gap-[clamp(0.85rem,1.4vw,1.35rem)] px-[clamp(1.1rem,2vw,1.9rem)] py-[clamp(0.45rem,1.1vh,1.1rem)] ${
                              item.image_url ? 'grid-cols-[auto_minmax(0,1fr)_auto]' : 'grid-cols-[minmax(0,1fr)_auto]'
                            }`}
                          >
                            {item.image_url && (
                              <div
                                className="relative aspect-square h-[clamp(3.6rem,8.2vh,5.8rem)] overflow-hidden border border-white/10 bg-white/[0.04] shadow-md shrink-0"
                                style={{ borderRadius: `calc(var(--brand-radius) * 0.6)` }}
                              >
                                <Utensils className="absolute left-1/2 top-1/2 h-7 w-7 -translate-x-1/2 -translate-y-1/2 text-white/20" />
                                <img
                                  src={optimizeImageUrl(item.image_url, { width: 320, quality: 88 })}
                                  alt={t(item, 'name')}
                                  onError={(event) => {
                                    event.currentTarget.style.display = 'none';
                                  }}
                                  className="relative h-full w-full object-cover"
                                />
                              </div>
                            )}

                            <div className="min-w-0">
                              <div className="mb-1 flex items-center gap-2">
                                {item.is_happy_hour && (
                                  <span className="shrink-0 rounded-md bg-[var(--signage-accent)] px-2 py-0.5 text-[9px] font-black uppercase tracking-widest text-black shadow-sm">
                                    Special
                                  </span>
                                )}
                                <h3 className="line-clamp-1 text-[clamp(1.1rem,1.7vw,1.95rem)] font-black leading-tight tracking-tight text-white">
                                  {t(item, 'name')}
                                </h3>
                              </div>
                              {t(item, 'description') && (
                                <p className="line-clamp-2 text-[clamp(0.78rem,1.05vw,1.15rem)] font-medium leading-normal text-white/60">
                                  {t(item, 'description')}
                                </p>
                              )}
                            </div>

                            <div className="min-w-28 text-right shrink-0">
                              {item.is_happy_hour && item.original_price && (
                                <p className="text-sm font-bold text-white/40 line-through tabular-nums">
                                  {formatPrice(item.original_price)}
                                </p>
                              )}
                              <p className="text-[clamp(1.4rem,2.2vw,2.65rem)] font-black leading-none text-[var(--signage-accent)] tabular-nums tracking-tight font-mono">
                                {formatPrice(item.price)}
                              </p>
                            </div>
                          </motion.article>
                        ))}
                      </div>
                    </section>
                  ))}
                </motion.div>
              </AnimatePresence>

              {showFeatured ? (
                <footer className="grid h-[clamp(5.2rem,13vh,8.2rem)] shrink-0 grid-cols-1 gap-5 lg:grid-cols-[minmax(0,1fr)_minmax(16rem,23rem)]">
                  <div
                    className={`grid overflow-hidden border border-white/12 bg-gradient-to-r from-[var(--signage-accent)]/10 via-white/[0.03] to-transparent items-center gap-6 px-6 shadow-xl backdrop-blur-md ${
                      featuredItem?.image_url ? 'grid-cols-[8.5rem_minmax(0,1fr)_auto]' : 'grid-cols-[minmax(0,1fr)_auto]'
                    }`}
                    style={{ borderRadius: 'var(--brand-radius)' }}
                  >
                    {featuredItem?.image_url && (
                      <div
                        className="relative aspect-square h-[clamp(3.8rem,9.5vh,6.2rem)] overflow-hidden border border-white/15 bg-black/40 shadow-lg shrink-0"
                        style={{ borderRadius: `calc(var(--brand-radius) * 0.75)` }}
                      >
                        <Sparkles className="absolute left-1/2 top-1/2 h-8 w-8 -translate-x-1/2 -translate-y-1/2 text-brand-primary/60" />
                        <img
                          src={optimizeImageUrl(featuredItem.image_url, { width: 360, quality: 88 })}
                          alt={t(featuredItem, 'name')}
                          onError={(event) => {
                            event.currentTarget.style.display = 'none';
                          }}
                          className="relative h-full w-full object-cover"
                        />
                      </div>
                    )}

                    <div className="min-w-0">
                      <div className="min-w-0">
                        <div className="mb-1.5 flex items-center gap-2">
                          <span className="inline-flex items-center gap-1.5 rounded-full bg-[var(--signage-accent)]/20 border border-[var(--signage-accent)]/40 px-2.5 py-0.5 text-[10px] font-black uppercase tracking-[0.25em] text-[var(--signage-accent)]">
                            <Sparkles className="h-3.5 w-3.5" />
                            Featured
                          </span>
                        </div>
                        <h3 className="line-clamp-1 text-[clamp(1.45rem,2.4vw,2.35rem)] font-black leading-tight text-white">
                          {featuredItem ? t(featuredItem, 'name') : 'Chef Special'}
                        </h3>
                        {featuredItem && t(featuredItem, 'description') && (
                          <p className="mt-1 line-clamp-1 max-w-4xl text-sm md:text-base font-medium text-white/65">
                            {t(featuredItem, 'description')}
                          </p>
                        )}
                      </div>
                    </div>

                    {featuredItem && (
                      <p className="shrink-0 text-[clamp(1.85rem,3.2vw,3.2rem)] font-black leading-none text-[var(--signage-accent)] tabular-nums font-mono tracking-tight">
                        {formatPrice(featuredItem.price)}
                      </p>
                    )}
                  </div>

                  <div
                    className="hidden border border-white/10 bg-white/[0.03] backdrop-blur-md p-5 lg:flex lg:flex-col lg:justify-between shadow-lg"
                    style={{ borderRadius: 'var(--brand-radius)' }}
                  >
                    <div className="mb-2 flex items-center justify-between gap-3">
                      <p className="text-[10px] font-black uppercase tracking-[0.28em] text-white/45">Menu Pages</p>
                      <p className="text-[11px] font-black text-[var(--signage-accent)] font-mono tabular-nums">{activePageIndex + 1} / {categoryPages.length}</p>
                    </div>
                    <div className="flex items-center gap-2 w-full">
                      {categoryPages.map((_, index) => (
                        <button
                          key={index}
                          type="button"
                          onClick={() => setActiveIndex(index)}
                          aria-label={`Show menu page ${index + 1}`}
                          className={`flex-1 h-3 rounded-full transition-all duration-300 cursor-pointer ${
                            index === activePageIndex
                              ? 'bg-[var(--signage-accent)] shadow-[0_0_12px_rgba(var(--color-brand-primary-rgb),0.8)]'
                              : 'bg-white/15 hover:bg-white/25'
                          }`}
                        />
                      ))}
                    </div>
                  </div>
                </footer>
              ) : (
                <footer
                  className="flex h-14 shrink-0 items-center justify-between border border-white/10 bg-white/[0.03] backdrop-blur-md px-6 shadow-lg"
                  style={{ borderRadius: 'var(--brand-radius)' }}
                >
                  <div className="flex items-center gap-3">
                    <span className="text-[10px] font-black uppercase tracking-[0.25em] text-white/45">
                      Page {activePageIndex + 1} of {categoryPages.length}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 max-w-sm w-full">
                    {categoryPages.map((_, index) => (
                      <button
                        key={index}
                        type="button"
                        onClick={() => setActiveIndex(index)}
                        aria-label={`Show menu page ${index + 1}`}
                        className={`flex-1 h-2.5 rounded-full transition-all duration-300 cursor-pointer ${
                          index === activePageIndex
                            ? 'bg-[var(--signage-accent)] shadow-[0_0_10px_rgba(var(--color-brand-primary-rgb),0.8)]'
                            : 'bg-white/15 hover:bg-white/25'
                        }`}
                      />
                    ))}
                  </div>
                </footer>
              )}
            </div>
          )}
        </section>

        {/* Screen Device Diagnostics & Pairing Dialog */}
        <AnimatePresence>
          {isPairDialogOpen && (
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-md">
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                className="w-full max-w-md rounded-2xl border border-white/10 bg-[#121212] p-6 shadow-2xl"
              >
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <Tv className="w-5 h-5 text-brand-primary" />
                    <h3 className="text-lg font-bold text-white">TV Screen Pairing</h3>
                  </div>
                  <button
                    type="button"
                    onClick={() => { setIsPairDialogOpen(false); setAuthError(''); }}
                    className="p-1 rounded-lg text-gray-400 hover:text-white hover:bg-white/5 transition-colors cursor-pointer"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>

                {activeScreenId ? (
                  <div className="space-y-4">
                    <div className="rounded-xl border border-emerald-500/20 bg-emerald-500/5 p-4">
                      <div className="flex items-center gap-2 text-emerald-400 mb-1">
                        <CheckCircle2 className="w-4 h-4" />
                        <span className="text-xs font-bold uppercase tracking-wider">Device Paired</span>
                      </div>
                      <p className="text-white font-bold text-base">
                        {activeScreenName || 'Registered Screen'}
                      </p>
                      <p className="text-xs text-gray-400 font-mono mt-1">
                        ID: {activeScreenId}
                      </p>
                    </div>

                    <p className="text-xs text-gray-400 leading-relaxed">
                      This screen receives live menu updates, remote reloads, and heartbeat diagnostics from your TableGreet Admin Dashboard.
                    </p>

                    <button
                      type="button"
                      onClick={() => {
                        if (!venue?.id) return;
                        localStorage.removeItem(getSignageScreenIdKey(venue.id));
                        setActiveScreenId(null);
                        setActiveScreenName(null);
                        setIsPairDialogOpen(false);
                      }}
                      className="w-full py-2.5 rounded-xl border border-red-500/30 bg-red-500/10 text-red-400 font-bold text-xs uppercase tracking-wider hover:bg-red-500/20 transition-colors cursor-pointer"
                    >
                      Unlink Screen from Device Fleet
                    </button>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <p className="text-xs text-gray-400 leading-relaxed">
                      Pair this TV browser into your venue's fleet using a 6-character code generated in the Admin Dashboard.
                    </p>

                    <form onSubmit={handlePairCodeSubmit} className="space-y-3">
                      <input
                        type="text"
                        value={pairingCodeInput}
                        onChange={(e) => setPairingCodeInput(e.target.value.toUpperCase())}
                        placeholder="e.g. A3F8K2"
                        className="w-full h-12 rounded-xl border border-white/10 bg-black/40 px-4 font-mono font-black text-center tracking-[0.35em] text-lg text-white outline-none transition-colors placeholder:text-gray-600 focus:border-brand-primary uppercase"
                        required
                        disabled={isVerifying}
                        maxLength={6}
                        autoComplete="off"
                        spellCheck={false}
                      />

                      {authError && (
                        <p role="alert" className="text-xs font-bold text-red-400">
                          {authError}
                        </p>
                      )}

                      <button
                        type="submit"
                        disabled={isVerifying || pairingCodeInput.trim().length < 4}
                        className="w-full h-11 rounded-xl bg-brand-primary text-black font-black uppercase tracking-widest text-xs transition-transform active:scale-[0.98] disabled:opacity-50 flex items-center justify-center gap-2 cursor-pointer"
                      >
                        {isVerifying ? (
                          <div className="w-4 h-4 border-2 border-black border-t-transparent rounded-full animate-spin" />
                        ) : (
                          <>
                            <Tv size={14} />
                            Link Display
                          </>
                        )}
                      </button>
                    </form>
                  </div>
                )}
              </motion.div>
            </div>
          )}
        </AnimatePresence>
      </main>
    </RTLWrapper>
  );
};
